/**
 * Telegram-style post mockup — used in the single creative preview on the
 * editor and in the "Все креативы" modal. Tries to look like a real channel
 * post: avatar + name + subscribers, time, photo grid, message body with
 * inline format/emoji/links, and the views / share / comment footer.
 */
import { Eye, MessageSquare, Forward } from "lucide-react";
import { useMyMedia } from "@/shared/api/wb";
import { renderCreativeText, type VariantMedia } from "./CreativeEditor";

type Post = {
  name: string;       // variant name, shown as a small lime corner badge
  channelName?: string;
  subscribers?: string;
  text: string;
  media: VariantMedia[];
  link?: string;
  erid?: string;
  eridOnImage?: boolean;
  time?: string;      // e.g. "14:21"
  views?: string;     // "12 432"
  forwards?: string;
  comments?: string;
};

const PLATFORM_THEME: Record<string, { grad: string; accent: string; label: string }> = {
  tg:  { grad: "linear-gradient(135deg,#5BB1E5,#229ED9)", accent: "#229ED9", label: "Telegram" },
  vk:  { grad: "linear-gradient(135deg,#3d8fe0,#0077FF)", accent: "#0077FF", label: "VK" },
  max: { grad: "linear-gradient(135deg,#9d5ff5,#7c3aed)", accent: "#7c3aed", label: "MAX" },
};

export function TelegramPost({ p, compact, platform = "tg" }: { p: Post; compact?: boolean; platform?: string }) {
  const theme = PLATFORM_THEME[platform] ?? PLATFORM_THEME.tg;
  const myEmoji = useMyMedia("emoji").data ?? [];
  const emojiMap = new Map(myEmoji.filter((e) => e.label).map((e) => [e.label!, e]));
  const html = renderCreativeText(p.text, emojiMap);
  return (
    <div className="relative rounded-lg max-w-[500px] mx-auto overflow-hidden"
      style={{ background: "#FFFFFF", border: "1px solid #E7EEF3" }}
    >

      {(p.channelName || p.subscribers) && (
        <div className="flex items-center gap-2.5 px-4 pt-3.5 pb-2.5">
          <div className="w-10 h-10 rounded-full grid place-items-center text-white font-display font-bold text-[15px] flex-shrink-0"
            style={{ background: theme.grad }}>
            {(p.channelName ?? "К").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[14.5px]" style={{ color: "#0F1419" }}>{p.channelName}</div>
            <div className="text-[12px]" style={{ color: "#8B98A5" }}>
              {p.subscribers} · реклама
            </div>
          </div>
        </div>
      )}

      {p.media.length > 0 && (
        <PhotoGrid
          media={p.media.slice(0, 4)}
          eridOverlay={p.eridOnImage ? (p.erid ?? "Kra24qhBV") : undefined}
        />
      )}

      <div className="px-4 py-2.5 text-[14.5px] leading-[1.4]" style={{ color: "#0F1419" }}>
        <div dangerouslySetInnerHTML={{ __html: html }} />
        {p.link && (
          <div className="mt-2 px-3 py-2.5 rounded-md border-l-[3px] text-[13px] truncate"
            style={{ background: "#F1F7FC", borderLeftColor: theme.accent, color: "#0F1419" }}>
            <div className="font-semibold text-[13.5px] truncate" style={{ color: theme.accent }}>{p.link}</div>
          </div>
        )}
      </div>

      <div className="px-4 pb-2.5 flex items-center justify-between text-[12px]"
        style={{ color: "#8B98A5" }}>
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1"><Eye size={13} /> {p.views ?? "12 432"}</span>
          <span className="inline-flex items-center gap-1"><Forward size={13} /> {p.forwards ?? "38"}</span>
          <span className="inline-flex items-center gap-1"><MessageSquare size={13} /> {p.comments ?? "6"}</span>
        </div>
        <span>{p.time ?? "14:21"}</span>
      </div>

      {!(p.eridOnImage && p.media.length > 0) && (
        <div className="px-4 pb-3 text-[10.5px] font-mono" style={{ color: "#8B98A5" }}>
          erid: {p.erid ?? "Kra24qhBV"} · реклама · ООО «WOWBLOGGER»
        </div>
      )}
    </div>
  );
}

function PhotoGrid({ media, eridOverlay }: { media: VariantMedia[]; eridOverlay?: string }) {
  if (media.length === 1) {
    return <Cell m={media[0]} className="aspect-[16/10]" eridOverlay={eridOverlay} />;
  }
  if (media.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-[2px]" style={{ background: "#E7EEF3" }}>
        <Cell m={media[0]} className="aspect-[5/6]" eridOverlay={eridOverlay} />
        <Cell m={media[1]} className="aspect-[5/6]" />
      </div>
    );
  }
  if (media.length === 3) {
    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-[2px]" style={{ background: "#E7EEF3" }}>
        <Cell m={media[0]} className="row-span-2 aspect-[3/5]" eridOverlay={eridOverlay} />
        <Cell m={media[1]} className="aspect-[5/3]" />
        <Cell m={media[2]} className="aspect-[5/3]" />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 grid-rows-2 gap-[2px]" style={{ background: "#E7EEF3" }}>
      <Cell m={media[0]} className="aspect-square" eridOverlay={eridOverlay} />
      <Cell m={media[1]} className="aspect-square" />
      <Cell m={media[2]} className="aspect-square" />
      <Cell m={media[3]} className="aspect-square" />
    </div>
  );
}

function Cell({ m, className, eridOverlay }: { m: VariantMedia; className: string; eridOverlay?: string }) {
  if (m.kind === "video") {
    return (
      <div className={`relative ${className} bg-[#0F1419]`}>
        <div className="absolute inset-0 grid place-items-center">
          <div className="w-12 h-12 rounded-full bg-black/50 grid place-items-center text-white text-[18px]">▶</div>
        </div>
        {eridOverlay && <EridOverlay text={eridOverlay} />}
      </div>
    );
  }
  return (
    <div className={`relative ${className}`}>
      <img src={m.url} alt="" className="block w-full h-full object-cover" />
      {eridOverlay && <EridOverlay text={eridOverlay} />}
    </div>
  );
}

function EridOverlay({ text }: { text: string }) {
  return (
    <div
      className="absolute bottom-0 left-0 right-0 px-2 py-1 text-[9px] font-mono leading-tight"
      style={{ background: "rgba(0,0,0,0.52)", color: "rgba(255,255,255,0.85)" }}
    >
      erid: {text} · реклама · ООО «WOWBLOGGER»
    </div>
  );
}
